/**
 * Markup out of whatever somebody handed us.
 *
 * Three ways in, one answer: a `.html` file, a `.json` file holding a design
 * this product saved, or markup pasted straight into the box. They are one
 * function because they are one question — "what is the email?" — and two
 * readers of the same file would be two places for the answer to differ.
 *
 * **The json shape is this product's own.** An `EmailBody` is stored as
 * `{ kind, html, source }`, so a design lifted out of the database or out of an
 * api response is a file this accepts without anybody writing an exporter. A
 * bare `{ "html": "…" }` is the same thing with less around it. Anything else
 * is refused by name rather than half-read: a json file that is not a design is
 * far more likely to be the wrong file than a design we failed to understand.
 *
 * Pure, and separate from the dialog, because what counts as a design is worth
 * a test and a file picker is not.
 */

export type Uploaded =
  | { readonly status: 'markup'; readonly html: string }
  | { readonly status: 'empty' }
  | { readonly status: 'not-a-design' }

export function uploadedMarkup(fileName: string, text: string): Uploaded {
  if (text.trim() === '') return { status: 'empty' }

  return isJson(fileName, text) ? fromJson(text) : { status: 'markup', html: text }
}

/**
 * Decided by the name, and by the text when there is no name.
 *
 * Pasted markup has no file name, and a person who pastes a design's json
 * rather than its html means the same thing by it — so text that opens with a
 * brace is read as json whatever it arrived as.
 */
function isJson(fileName: string, text: string): boolean {
  return fileName.toLowerCase().endsWith('.json') || text.trimStart().startsWith('{')
}

function fromJson(text: string): Uploaded {
  try {
    const parsed: unknown = JSON.parse(text)
    if (typeof parsed !== 'object' || parsed === null) return { status: 'not-a-design' }

    const { html } = parsed as { html?: unknown }
    if (typeof html !== 'string' || html.trim() === '') return { status: 'not-a-design' }

    return { status: 'markup', html }
  } catch {
    return { status: 'not-a-design' }
  }
}
